'use client';

/**
 * SiteHeader — the persistent top interface of the laboratory.
 * Fixed, translucent, always visible; more transparent while the page rests
 * at the very top so the world shows through. Mobile (≤820px) collapses the
 * primary nav into a full-screen overlay menu.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useWorld } from '../../lab/store';
import { lockScroll, unlockScroll } from '../../lab/scrollLock';
import { primaryNav, secondaryNav, site } from '../../content/site';
import {
  langFromPathname,
  localePath,
  stripLocale,
  swapLocale,
  otherLang,
  LOCALE_NAME,
} from '../../i18n/config';
import { getDict } from '../../i18n/dictionary';
import s from './SiteHeader.module.css';

/** Compare the logical (locale-stripped) path against a logical nav href. */
function isActive(logicalPath: string, href: string): boolean {
  return logicalPath === href || logicalPath.startsWith(`${href}/`);
}

export default function SiteHeader() {
  const pathname = usePathname() ?? '/';
  const lang = langFromPathname(pathname);
  const logical = stripLocale(pathname);
  const dict = getDict(lang);
  const audioOn = useWorld((w) => w.audioOn);

  const [atTop, setAtTop] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  const burgerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  /* ── transparency at top of page ─────────────────────────── */
  useEffect(() => {
    const onScroll = () => setAtTop(window.scrollY < 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ── mobile menu lifecycle ───────────────────────────────── */
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    burgerRef.current?.focus();
  }, []);

  // Close on route change (no focus restore — the user navigated away).
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return;

    lockScroll();
    closeRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu();
        return;
      }
      if (e.key !== 'Tab') return;
      // Cycle focus inside the overlay menu.
      const root = menuRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !root.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      unlockScroll();
    };
  }, [menuOpen, closeMenu]);

  const openPalette = () => useWorld.getState().setPaletteOpen(true);
  const toggleAudio = () => useWorld.getState().setAudio(!audioOn);

  return (
    <header className={`${s.header} ${atTop ? s.atTop : ''}`}>
      <div className={`wrap ${s.inner}`}>
        {/* ── identity ── */}
        <div className={s.identity}>
          <Link
            href={localePath(lang, '/')}
            className={s.wordmark}
            aria-label={`${site.name} — ${dict.header.homeAria}`}
          >
            STUDYLABPRO
          </Link>
          <span className={s.node} aria-hidden="true">
            <span className={`status-dot ${s.nodeDot}`} />
            <span className={`machine--sm ${s.nodeText}`}>{dict.header.nodeOnline}</span>
          </span>
        </div>

        {/* ── primary navigation (desktop) ── */}
        <nav className={s.nav} aria-label="Primary">
          <ul className={s.navList}>
            {primaryNav.map((item) => {
              const active = isActive(logical, item.href);
              return (
                <li key={item.href}>
                  <Link
                    href={localePath(lang, item.href)}
                    className={`${s.navLink} ${active ? s.navLinkActive : ''}`}
                    aria-current={active ? 'page' : undefined}
                  >
                    {dict.nav[item.key]}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* ── controls ── */}
        <div className={s.controls}>
          <Link
            href={swapLocale(pathname, otherLang(lang))}
            className={`machine--sm ${s.audio} ${s.langSwitch}`}
            aria-label={dict.header.langSwitchAria}
            hrefLang={otherLang(lang)}
          >
            {LOCALE_NAME[otherLang(lang)]}
          </Link>
          <button
            type="button"
            className={s.ask}
            onClick={openPalette}
            data-lab-ask-trigger
            aria-label={dict.header.askAria}
          >
            {dict.header.ask} <kbd className={s.kbd}>⌘K</kbd>
          </button>
          <button
            type="button"
            className={`machine--sm ${s.audio}`}
            onClick={toggleAudio}
            aria-pressed={audioOn}
            aria-label={audioOn ? dict.header.audioOnAria : dict.header.audioOffAria}
          >
            AUDIO {audioOn ? 'ON' : 'OFF'}
          </button>
          <button
            type="button"
            ref={burgerRef}
            className={s.burger}
            onClick={() => setMenuOpen(true)}
            aria-expanded={menuOpen}
            aria-controls="site-menu"
            aria-label={dict.header.openMenu}
          >
            <span className={s.burgerBar} aria-hidden="true" />
            <span className={s.burgerBar} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* ── mobile full-screen menu ── */}
      {menuOpen && (
        <div
          id="site-menu"
          ref={menuRef}
          className={s.menu}
          role="dialog"
          aria-modal="true"
          aria-label={dict.header.menuAria}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeMenu();
          }}
        >
          <div className={s.menuTop}>
            <span className={`machine--sm ${s.menuMark}`}>STUDYLABPRO</span>
            <div className={s.menuTopActions}>
              <Link
                href={swapLocale(pathname, otherLang(lang))}
                className={`machine--sm ${s.menuClose}`}
                aria-label={dict.header.langSwitchAria}
                hrefLang={otherLang(lang)}
                onClick={closeMenu}
              >
                {LOCALE_NAME[otherLang(lang)]}
              </Link>
              <button
                type="button"
                ref={closeRef}
                className={`machine--sm ${s.menuClose}`}
                onClick={closeMenu}
              >
                {dict.header.close} ✕
              </button>
            </div>
          </div>

          <nav className={s.menuNav} aria-label="All sections">
            <p className={`label ${s.menuGroupLabel}`}>{dict.header.groupLab}</p>
            <ul className={s.menuList}>
              {primaryNav.map((item) => {
                const active = isActive(logical, item.href);
                return (
                  <li key={item.href}>
                    <Link
                      href={localePath(lang, item.href)}
                      className={`${s.menuLink} ${active ? s.menuLinkActive : ''}`}
                      aria-current={active ? 'page' : undefined}
                    >
                      {dict.nav[item.key]}
                    </Link>
                  </li>
                );
              })}
            </ul>

            <p className={`label ${s.menuGroupLabel}`}>{dict.header.groupIndex}</p>
            <ul className={s.menuList}>
              {secondaryNav.map((item) => {
                const active = isActive(logical, item.href);
                return (
                  <li key={item.href}>
                    <Link
                      href={localePath(lang, item.href)}
                      className={`${s.menuLink} ${active ? s.menuLinkActive : ''}`}
                      aria-current={active ? 'page' : undefined}
                    >
                      {dict.nav[item.key]}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className={s.menuFoot}>
            <button
              type="button"
              className={`machine--sm ${s.audio} ${s.menuAudio}`}
              onClick={toggleAudio}
              aria-pressed={audioOn}
              aria-label={audioOn ? dict.header.audioOnAria : dict.header.audioOffAria}
            >
              AUDIO {audioOn ? 'ON' : 'OFF'}
            </button>
            <p className={`machine--sm ${s.menuNode}`}>
              <span className="status-dot" /> {dict.header.nodeOnline} ·{' '}
              {getDict(lang).footer.tagline.toUpperCase()}
            </p>
          </div>
        </div>
      )}
    </header>
  );
}
